import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useSubscription } from "@apollo/client";
import { BellOff, Search } from "lucide-react";
import { formatRelativeToNow } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { CONVERSATION_UPDATED, MY_CONVERSATIONS } from "./queries";
import type { ChatConversation } from "./types";

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
  currentUserId: string;
}

export function ConversationList({ selectedId, onSelect, currentUserId }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data, loading, refetch } = useQuery(MY_CONVERSATIONS);

  useSubscription(CONVERSATION_UPDATED, {
    variables: { userId: currentUserId },
    onData: () => refetch(),
  });

  const conversations = (data?.myConversations ?? []) as ChatConversation[];
  const filtered = conversations.filter((c) => {
    if (!search) return true;
    const name = c.otherParticipant
      ? `${c.otherParticipant.firstName} ${c.otherParticipant.lastName}`
      : "";
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <aside className="flex h-full w-full flex-col border-r border-border bg-background md:w-[320px]">
      <div className="px-4 pt-5 pb-3">
        <h1 className="font-display text-[20px] font-semibold leading-none tracking-tight">
          {t("chat.title")}
        </h1>
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("chat.searchPlaceholder")}
            className="h-9 w-full rounded-full border border-border bg-secondary/60 pl-9 pr-3 text-base outline-none md:text-[13px] placeholder:text-muted-foreground focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/30"
          />
        </div>
      </div>

      <nav className="scrollbar-thin flex-1 overflow-y-auto pb-4">
        {loading ? (
          <div className="px-4 py-6 text-center text-[12px] text-muted-foreground">
            {t("chat.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 text-center text-[12px] text-muted-foreground">
            {t("chat.noConversations")}
          </div>
        ) : (
          filtered.map((c) => (
            <Row
              key={c.id}
              c={c}
              selected={c.id === selectedId}
              onClick={() => onSelect(c.id)}
            />
          ))
        )}
      </nav>
    </aside>
  );
}

function Row({
  c,
  selected,
  onClick,
}: {
  c: ChatConversation;
  selected: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const other = c.otherParticipant;
  const lastBody = c.lastMessage?.deletedForEveryone
    ? t("chat.messageDeleted")
    : c.lastMessage?.body ?? t("chat.noMessagesYet");
  const lastTime = c.lastMessageAt
    ? formatRelativeToNow(c.lastMessageAt, i18n.language, { addSuffix: false })
    : "";
  const isFounder = c.isSupportThread;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors focus-ring",
        "hover:bg-secondary/60",
        selected && "bg-secondary/70",
      )}
    >
      {selected && (
        <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-primary" />
      )}
      <Avatar initials={other?.initials ?? "?"} online={other?.online} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "truncate text-[14px] font-medium",
              c.muted && "opacity-70",
            )}
          >
            {other ? `${other.firstName} ${other.lastName}` : t("chat.conversation")}
          </span>
          {isFounder && (
            <span className="rounded-full bg-primary/15 px-1.5 py-px text-[10px] font-medium leading-tight text-primary ring-1 ring-primary/30">
              {t("chat.founderBadge")}
            </span>
          )}
          <span className="ml-auto flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
            {c.muted && <BellOff className="size-3" />}
            {lastTime}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <p className="truncate text-[13px] text-muted-foreground">{lastBody}</p>
          {c.unreadCount > 0 && (
            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {c.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function Avatar({ initials, online }: { initials: string; online?: boolean }) {
  return (
    <div className="relative shrink-0">
      <div className="flex size-10 items-center justify-center rounded-full bg-secondary text-[13px] font-semibold text-muted-foreground ring-1 ring-border">
        {initials}
      </div>
      {online && (
        <span className="absolute bottom-0 right-0 size-2.5 rounded-full bg-success ring-2 ring-background" />
      )}
    </div>
  );
}
